import { useState } from 'react';
import { useParams } from 'react-router-dom';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import useDeleteConfirmation from './useDeleteConfirmation';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import { DefaultMultiPlaintiffName } from '../utils/constant';
import { getDefaultDefendant } from '../utils/formSchema';
import { buildDefendantUpdatePayload, buildDefendantAddPayload } from '../utils/caseFieldPaths';
import { UPDATE_CASE_ACTION } from '@demandproai/shared';
import useLiabilityAnalysisForm from './useLiabilityAnalysisForm';

const useCaseDefendant = () => {
  const { formik, updateLiabilityFilesDefendants } = useAddCaseFormContext();
  const { isSingleLiabilityMode, setIsSingleLiabilitySection } = useLiabilityAnalysisForm();
  const { caseId } = useParams();
  const { updateCase, updateCaseById } = useCaseFieldUpdate({ caseId });

  // Modal state management
  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const openModal = (index) => {
    setSelectedIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedIndex(null);
  };

  const updateDefendant = ({ defendantObj, index }) => {
    const updated = [...formik.values.defendants];
    updated[index] = defendantObj;
    formik.setFieldValue('defendants', updated);
    updateCaseById(buildDefendantUpdatePayload([defendantObj]));
  };

  const addNewDefendant = (defendantObj) => {
    const currentDefendants = formik.values.defendants;
    const isReplacingDefault = (
      currentDefendants.length === 1 &&
      (currentDefendants[0]?.personalInfo?.name === DefaultMultiPlaintiffName.DEFAULT_DEFENDANT_Name ||
       !currentDefendants[0]?.personalInfo?.name)
    );

    if (isReplacingDefault) {
      defendantObj.defendantId = currentDefendants[0]?.defendantId;
      formik.setFieldValue('defendants', [defendantObj]);
      updateCaseById(buildDefendantUpdatePayload([defendantObj]));
    } else {
      const updatedDefendants = [...currentDefendants, defendantObj];
      formik.setFieldValue('defendants', updatedDefendants);
      updateCase({
        data: { defendants: buildDefendantAddPayload(defendantObj) },
        action: UPDATE_CASE_ACTION.ADD
      });

      const isSingleLiabilityMode = formik.values.caseInfo?.isSingleLiabilitySectionForAllDefendants;
      if (isSingleLiabilityMode) {
        updateLiabilityFilesDefendants(updatedDefendants);
      }
    }
  };

  const removeDefendantFromLiability = (defendantId) => {
    const liability = formik.values.liability;
    if (!liability) return liability;

    const updatedLiability = {};

    Object.entries(liability).forEach(([key, value]) => {
      if (!Array.isArray(value)) {
        updatedLiability[key] = value;
        return;
      }

      updatedLiability[key] = value
        .map(item => {
          if (!item?.defendants?.includes(defendantId)) return item;
          return { ...item, defendants: item.defendants.filter(id => id !== defendantId) };
        })
        .filter(item => {
          if (!item) return false;
          if (!Array.isArray(item.defendants)) return true;
          return item.defendants.length > 0;
        });
    });

    return updatedLiability;
  };

  const stripClientId = ({ id: _ignored, ...rest }) => rest;

  const buildLiabilityUpdatePayload = (liability) => {
    const data = {};
    Object.entries(liability || {}).forEach(([key, value]) => {
      data[`liability.${key}`] = Array.isArray(value) ? value.map(stripClientId) : value;
    });
    return data;
  };

  const deleteDefendant = (deletedIndex) => {
    const currentDefendants = formik.values.defendants;
    const defendantToDelete = currentDefendants[deletedIndex];
    const updatedLiability = removeDefendantFromLiability(defendantToDelete.defendantId);
    const liabilityUpdatePayload = buildLiabilityUpdatePayload(updatedLiability);

    if (currentDefendants.length === 1) {
      const resetDefendant = getDefaultDefendant();
      formik.setValues({
        ...formik.values,
        defendants: [resetDefendant],
        liability: updatedLiability
      });
      updateCase({
        data: { defendants: { defendantId: defendantToDelete.defendantId } },
        action: UPDATE_CASE_ACTION.REMOVE
      });
      updateCase({
        data: { defendants: buildDefendantAddPayload(resetDefendant) },
        action: UPDATE_CASE_ACTION.ADD
      });
      if (Object.keys(liabilityUpdatePayload).length > 0) {
        updateCase({ data: liabilityUpdatePayload });
      }
      return;
    }

    const updatedDefendants = currentDefendants.filter((_, index) => index !== deletedIndex);

    formik.setValues({
      ...formik.values,
      defendants: updatedDefendants,
      liability: updatedLiability
    });

    if (currentDefendants.length === 2 && !isSingleLiabilityMode) {
      setIsSingleLiabilitySection(true, updatedDefendants, updatedLiability);
    }

    updateCase({
      data: { defendants: { defendantId: defendantToDelete.defendantId } },
      action: UPDATE_CASE_ACTION.REMOVE
    });
    if (Object.keys(liabilityUpdatePayload).length > 0) {
      updateCase({ data: liabilityUpdatePayload });
    }
  };

  const deleteConfirm = useDeleteConfirmation({
    items: formik.values.defendants,
    onDelete: deleteDefendant,
  });

  return {
    updateDefendant,
    addNewDefendant,
    deleteDefendant,
    requestDeleteDefendant: deleteConfirm.request,
    confirmDeleteDefendant: deleteConfirm.confirm,
    cancelDeleteDefendant: deleteConfirm.cancel,
    pendingDeleteDefendant: deleteConfirm.pendingItem,
    isConfirmingDelete: deleteConfirm.isConfirming,
    showModal,
    selectedIndex,
    openModal,
    closeModal,
  };
};

export default useCaseDefendant;
